import _ from 'lodash'

import i18n from '@/i18n/i18n'
import ReportService from '@/services/admin/ReportService.js'
import { build_query, errorMessage } from '@/utilities/helpers.js'

const { t } = i18n.global

export const allowedFilters = [
  'retailer_id',
  'farmer_id',
  'region_id',
  'created_between',
  'completed_between',
  'payment_date_between',
  'sales_channels',
  'category_ids',
  'financing_bank_ids',
  'assignee_id',
  'invoice_date_between',
  'by_invoice',
  'original_platform',
  'income_protected_sale'
]
export const allowedIncludes = 'farmer,payments,retailer'
export const namespaced = true

export const state = {
  salesReport: null,
  reportSnapshots: [],
  underwritingDecisionReport: null,
  paymentPerformanceReports: null,
  financePortfolioReport: {
    weightedAverages: null,
    total: null,
    products: null,
    cities: null,
    counties: null,
    retailers: null,
    retailersAllTime: null,
    grownProducts: null
  },
  mobileReport: {
    ordersByPlatform: [],
    applicationsByPlatform: [],
    pricesByCity: {
      mapData: [],
      data: [],
      meta: null
    },
    pricesByRetailer: {
      data: [],
      meta: null
    }
  }
}

export const mutations = {
  SET_SALES_REPORT(state, report) {
    state.salesReport = report.data
  },
  SET_REPORT_SNAPSHOTS(state, report) {
    state.reportSnapshots = report.data
  },
  SET_PAYMENT_PERFORMANCE_REPORT(state, report) {
    state.paymentPerformanceReports = report.data
  },
  SET_UNDERWRITING_DECISION_REPORT(state, report) {
    state.underwritingDecisionReport = report.data
  },
  SET_FINANCE_PORTFOLIO_REPORT_BY_WEIGHTED_AVERAGE(state, report) {
    state.financePortfolioReport.weightedAverages = report.data
  },
  SET_FINANCE_PORTFOLIO_REPORT_BY_PRODUCT(state, report) {
    state.financePortfolioReport.products = report.data.products
    state.financePortfolioReport.total = report.data.total
  },
  SET_FINANCE_PORTFOLIO_REPORT_BY_CITY(state, report) {
    state.financePortfolioReport.cities = report.data
  },
  SET_FINANCE_PORTFOLIO_REPORT_BY_COUNTY(state, report) {
    state.financePortfolioReport.counties = report.data
  },
  SET_FINANCE_PORTFOLIO_REPORT_BY_RETAILER(state, report) {
    state.financePortfolioReport.retailers = report.data.retailers
    state.financePortfolioReport.retailersAllTime = report.data.retailersAllTime
    state.financePortfolioReport.totalRetailer = report.data.totalRetailer
  },
  SET_FINANCE_PORTFOLIO_REPORT_BY_GROWN_PRODUCT(state, report) {
    state.financePortfolioReport.grownProducts = report.data
  },
  SET_COUNTIES_BY_CITY(state, cityId) {
    let row = state.financePortfolioReport.cities.items.find(city => city.id === cityId)
    if (!row) {
      return
    }
    row.sub = state.financePortfolioReport.counties.items.filter(county => county.city_id === cityId)
  },
  SET_MOBILE_REPORT_ORDERS_BY_PLATFORM(state, report) {
    state.mobileReport.ordersByPlatform = report.data
  },
  SET_MOBILE_REPORT_APPLICATIONS_BY_PLATFORM(state, report) {
    state.mobileReport.applicationsByPlatform = report.data
  },
  SET_MOBILE_REPORT_PRICES_BY_CITY_FOR_MAP_DATA(state, report) {
    state.mobileReport.pricesByCity.mapData = report.data
  },
  SET_MOBILE_REPORT_PRICES_BY_CITY_FOR_DATA(state, params) {
    state.mobileReport.pricesByCity.meta = params.response.meta
    let cities = _.map(params.response.data, item => {
      return _.extend({ sub: null }, item)
    })

    if (params.page > 1) {
      cities.forEach(city => {
        state.mobileReport.pricesByCity.data.push(city)
      })
    } else {
      state.mobileReport.pricesByCity.data = cities
    }
  },
  SET_MOBILE_REPORT_PRICES_BY_PRODUCT_IN_CITY(state, params) {
    let priceByCity = state.mobileReport.pricesByCity.data.find(item => {
      return item.id === params.cityId
    })

    if (!priceByCity) {
      return
    }

    priceByCity.sub = params.report
  },
  SET_MOBILE_REPORT_PRICES_BY_RETAILER(state, params) {
    state.mobileReport.pricesByRetailer.meta = params.response.meta

    let retailers = _.map(params.response.data, item => {
      return _.extend({ sub: null }, item)
    })

    if (params.page > 1) {
      retailers.forEach(retailer => {
        state.mobileReport.pricesByRetailer.data.push(retailer)
      })
    } else {
      state.mobileReport.pricesByRetailer.data = retailers
    }
  },
  SET_MOBILE_REPORT_PRICES_BY_PRODUCT_FOR_RETAILER(state, params) {
    let priceByRetailer = state.mobileReport.pricesByRetailer.data.find(item => {
      return item.id === params.retailerId
    })

    if (!priceByRetailer) {
      return
    }

    priceByRetailer.sub = params.report
  }
}

export const actions = {
  getSalesReports({ commit, dispatch }, query) {
    commit('SET_SALES_REPORT', { data: null })
    let newQuery = build_query(query, allowedFilters, allowedIncludes)
    return new Promise((resolve, reject) => {
      ReportService.cancelSalesReportRequest()
      ReportService.getSalesReports(newQuery)
        .then(response => {
          commit('SET_SALES_REPORT', response)
          resolve(response)
        })
        .catch(err => {
          if (err.constructor.name !== 'Cancel') {
            const notification = {
              type: 'danger',
              message: t('store.admin.report.get_sales_reports_error') + errorMessage(err)
            }
            dispatch('notification/add', notification, {
              root: true
            })
            reject(err)
          }
        })
    })
  },
  getUnderwritingDecisionReports({ commit, dispatch }, query) {
    let newQuery = build_query(query, allowedFilters, null)
    return new Promise((resolve, reject) => {
      ReportService.getUnderwritingDecisionReports(newQuery)
        .then(response => {
          commit('SET_UNDERWRITING_DECISION_REPORT', response)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.admin.report.get_underwriting_decision_report_error') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getPaymentPerformanceReports({ commit, dispatch }, query) {
    let newQuery = build_query(query, allowedFilters, allowedIncludes)
    return new Promise((resolve, reject) => {
      ReportService.getRetailerPerformanceReports(newQuery)
        .then(response => {
          commit('SET_PAYMENT_PERFORMANCE_REPORT', response)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.admin.report.get_payment_performance_reports_error') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getFinancePortfolioReportByWeightedAverage({ commit, dispatch }, query) {
    let newQuery = build_query(query, allowedFilters, allowedIncludes)

    return new Promise((resolve, reject) => {
      ReportService.getFinancePortfolioReportsByWeightedAverage(newQuery)
        .then(response => {
          commit('SET_FINANCE_PORTFOLIO_REPORT_BY_WEIGHTED_AVERAGE', response)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.admin.report.get_finance_portfolio_report_by_weighted_average_error') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getFinancePortfolioReportByProduct({ commit, dispatch }, query) {
    let newQuery = build_query(query, allowedFilters, allowedIncludes)

    return new Promise((resolve, reject) => {
      ReportService.getFinancePortfolioReportsByProduct(newQuery)
        .then(response => {
          commit('SET_FINANCE_PORTFOLIO_REPORT_BY_PRODUCT', response)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.admin.report.get_finance_portfolio_report_by_product') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getFinancePortfolioReportByCity({ commit, dispatch }, query) {
    let newQuery = build_query(query, allowedFilters, allowedIncludes)

    return new Promise((resolve, reject) => {
      ReportService.getFinancePortfolioReportsByCity(newQuery)
        .then(response => {
          commit('SET_FINANCE_PORTFOLIO_REPORT_BY_CITY', response)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.admin.report.get_finance_portfolio_report_by_city') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getFinancePortfolioReportByCounty({ commit, dispatch }, query) {
    let newQuery = build_query(query, allowedFilters, allowedIncludes)

    return new Promise((resolve, reject) => {
      ReportService.getFinancePortfolioReportsByCounty(newQuery)
        .then(response => {
          commit('SET_FINANCE_PORTFOLIO_REPORT_BY_COUNTY', response)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.admin.report.get_finance_portfolio_report_by_county') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getFinancePortfolioReportByRetailer({ commit, dispatch }, query) {
    let newQuery = build_query(query, allowedFilters, allowedIncludes)

    return new Promise((resolve, reject) => {
      ReportService.getFinancePortfolioReportsByRetailer(newQuery)
        .then(response => {
          commit('SET_FINANCE_PORTFOLIO_REPORT_BY_RETAILER', response)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.admin.report.get_finance_portfolio_report_by_retailer_error') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getFinancePortfolioReportByGrownProduct({ commit, dispatch }, query) {
    let newQuery = build_query(query, allowedFilters, allowedIncludes)

    return new Promise((resolve, reject) => {
      ReportService.getFinancePortfolioReportByGrownProduct(newQuery)
        .then(response => {
          commit('SET_FINANCE_PORTFOLIO_REPORT_BY_GROWN_PRODUCT', response)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.admin.report.get_finance_portfolio_report_by_grown_product') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getCountiesByCity({ commit }, cityId) {
    commit('SET_COUNTIES_BY_CITY', cityId)
  },
  getSnapshots({ commit, dispatch }, query) {
    let newQuery = build_query(query, allowedFilters, allowedIncludes)
    return new Promise((resolve, reject) => {
      ReportService.getSnapshots(newQuery)
        .then(response => {
          commit('SET_REPORT_SNAPSHOTS', response)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.admin.report.get_snapshots_error') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  loadSnapshot({ commit }, snapshot) {
    commit('SET_SALES_REPORT', { data: snapshot })
  },
  getOrdersByPlatform({ commit, dispatch }, query) {
    let newQuery = build_query(query, ['created_between'], null)
    return new Promise((resolve, reject) => {
      ReportService.getOrdersByPlatform(newQuery)
        .then(response => {
          commit('SET_MOBILE_REPORT_ORDERS_BY_PLATFORM', response)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.admin.report.get_orders_by_platform_error') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getApplicationsByPlatform({ commit, dispatch }, query) {
    let newQuery = build_query(query, ['created_between'], null)
    return new Promise((resolve, reject) => {
      ReportService.getApplicationsByPlatform(newQuery)
        .then(response => {
          commit('SET_MOBILE_REPORT_APPLICATIONS_BY_PLATFORM', response)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.admin.report.get_applications_by_platform_error') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getMobilePriceReportByCity({ commit, dispatch }, query) {
    let newQuery = build_query(query, ['created_between'], null)
    return new Promise((resolve, reject) => {
      ReportService.getMobilePricesByCity(newQuery)
        .then(response => {
          if (query.page) {
            commit('SET_MOBILE_REPORT_PRICES_BY_CITY_FOR_DATA', {
              response,
              page: query.page
            })
          } else {
            commit('SET_MOBILE_REPORT_PRICES_BY_CITY_FOR_MAP_DATA', response)
          }
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.admin.report.get_mobile_price_report_by_city_error') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getMobilePricesByProductInCity({ dispatch, commit }, params) {
    let newQuery = build_query(params.query, ['created_between'], null)
    return new Promise((resolve, reject) => {
      ReportService.getMobilePricesByProductInCity(params.cityId, newQuery)
        .then(response => {
          commit('SET_MOBILE_REPORT_PRICES_BY_PRODUCT_IN_CITY', {
            cityId: params.cityId,
            report: response.data
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.admin.report.get_mobile_prices_by_product_in_city_error') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getMobilePriceReportByRetailer({ commit, dispatch }, query) {
    let newQuery = build_query(query, ['created_between'], null)
    return new Promise((resolve, reject) => {
      ReportService.getMobilePricesByRetailer(newQuery)
        .then(response => {
          if (query.page) {
            commit('SET_MOBILE_REPORT_PRICES_BY_RETAILER', {
              response,
              page: query.page
            })
          }
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.admin.report.get_mobile_price_report_by_retailer') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getMobilePricesByProductForRetailer({ dispatch, commit }, params) {
    let newQuery = build_query(params.query, ['created_between'], null)
    return new Promise((resolve, reject) => {
      ReportService.getMobilePricesByProductForRetailer(params.retailerId, newQuery)
        .then(response => {
          commit('SET_MOBILE_REPORT_PRICES_BY_PRODUCT_FOR_RETAILER', {
            retailerId: params.retailerId,
            report: response.data
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.admin.report.get_mobile_price_report_by_product_for_retailer') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  }
}
